import Link from "next/link";
import { formatDateTime } from "../utils/dateFormatter";

export default function HeroPost({
  title,
  coverImage,
  date,
  author,
  slug,
  excerpt,
  averageRating = 0,
  tags = [],
  updatedAt,
}) {
  const published = date ? new Date(date) : null;
  const updated = updatedAt ? new Date(updatedAt) : null;

  const isValidPublished = published && !isNaN(published.getTime());
  const isValidUpdated = updated && !isNaN(updated.getTime());

  const wasUpdated = isValidPublished && isValidUpdated && updated > published;

  const formattedDateTime = isValidPublished ? formatDateTime(date) : "";
  const formattedUpdatedAt = isValidUpdated ? formatDateTime(updatedAt) : "";

  const safeRating = Math.max(0, Math.min(5, Math.round(averageRating)));

  const renderStars = () =>
    [...Array(5)].map((_, index) => (
      <svg
        key={index}
        xmlns="http://www.w3.org/2000/svg"
        className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8"
        fill={index < safeRating ? "#FF9346" : "none"}
        stroke={index < safeRating ? "#FF9346" : "#ccc"}
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
    ));

  return (
    <div className="hero-post relative w-full bg-[#005f59]">
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 h-full">
        <div className="relative flex items-center justify-center p-8 lg:p-16">
          <div className="absolute top-4 right-0 w-32 h-32 bg-[#FF9346] rounded-full opacity-50 z-0 hidden lg:block">
            <img
              src="/assets/svg/newssvgicon.svg"
              alt="News Icon"
              className="w-24 h-24 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            />
          </div>

          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#FF9346] rounded-full opacity-25 z-0 hidden lg:block">
            <img
              src="/assets/svg/blogs.svg"
              alt="Blogs Icon"
              className="w-24 h-24 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            />
          </div>

          <div className="relative group">
            <Link href={`/blogs/${slug}`}>
              <img
                src={coverImage}
                alt={title}
                className="relative z-10 w-full h-auto object-contain rounded-lg shadow-lg transition-transform duration-500 group-hover:scale-105"
              />
            </Link>

            <div className="absolute top-4 left-0 flex flex-wrap z-20 transition-transform duration-500 group-hover:scale-105">
              {tags.map((tag) => (
                <span
                  key={tag.id ?? tag.name}
                  className="bg-[#FF9346] text-white text-xs font-semibold mr-2 px-2.5 py-0.5 rounded-r-full"
                >
                  {tag.name}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col justify-center p-8 lg:p-16 text-white">
          <div className="max-w-2xl mx-auto lg:mx-0">
            <Link href={`/blogs/${slug}`}>
              <h2 className="text-lg sm:text-2xl lg:text-4xl font-bold mb-4 hover:text-[#FF9346] transition-colors line-clamp-3">
                {title}
              </h2>
            </Link>

            <p className="text-xs sm:text-sm lg:text-base text-gray-300 mb-2">
              {wasUpdated ? formattedUpdatedAt : formattedDateTime}
            </p>

            <div className="flex justify-between items-center mb-4">
              <p className="text-sm sm:text-base lg:text-lg font-bold">
                {author?.name ?? "Unknown author"}
              </p>

              <div className="flex items-center">{renderStars()}</div>
            </div>

            <p className="text-xs sm:text-sm lg:text-lg mb-4">{excerpt}</p>

            <Link
              href={`/blogs/${slug}`}
              className="bg-[#FF9346] w-full rounded-3xl px-6 py-2 hover:bg-[#e67e22] transition-colors shadow-md inline-block text-center"
            >
              Read More
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#005f59] to-transparent" />
    </div>
  );
}